import {
  LanguageDropdown,
  PersonaChip,
  personaState,
  type AddablePersona,
  type PersonaVariant,
} from "@/entities/persona";
import { AddPersonaButton } from "@/features/add-persona";
import type { Persona } from "@/lib/api";

interface PersonaStripProps {
  jobId: number;
  currentPersona: Persona;
  currentLanguage: string;
  variants: PersonaVariant[];
  /** Roles the user may add to this job (from available-variants). */
  addable: AddablePersona[];
  /** Source/original language code, used to label the "" (source) variant. */
  sourceLanguage?: string;
  onPersonaChange: (persona: Persona) => void;
  onLanguageChange: (language: string) => void;
}

/** Persona/language switcher plus the add action. Renders even with no variants —
 *  a job that has not produced a persona yet is exactly when the add button matters. */
export function PersonaStrip({
  jobId,
  currentPersona,
  currentLanguage,
  variants,
  addable,
  sourceLanguage,
  onPersonaChange,
  onLanguageChange,
}: PersonaStripProps) {
  return (
    <div className="px-6 pt-3 flex flex-wrap items-center gap-2 relative" data-qa="persona-strip">
      {variants.map((variant) => {
        const isActive = variant.persona === currentPersona;
        // The language dropdown belongs to the active chip only, and only once its
        // reports have settled — mid-generation the chip shows the countdown instead.
        const showDropdown =
          isActive && variant.languages.length >= 2 && personaState(variant) !== "generating";

        return (
          <PersonaChip
            key={variant.persona}
            variant={variant}
            isActive={isActive}
            sourceLanguage={sourceLanguage}
            onClick={() => onPersonaChange(variant.persona)}
            trailing={
              showDropdown ? (
                <LanguageDropdown
                  languages={variant.languages}
                  currentLanguage={currentLanguage}
                  sourceLanguage={sourceLanguage}
                  onLanguageChange={onLanguageChange}
                />
              ) : undefined
            }
          />
        );
      })}

      <AddPersonaButton jobId={jobId} addable={addable} variant="chip" placement="below" />
    </div>
  );
}
